import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import api from "../services/api";
import { useWorkspace } from "./WorkspaceContext";

const CustomViewsContext = createContext();

// Provides the user's CustomViews for the active workspace, plus CRUD helpers.
export const CustomViewsProvider = ({ children }) => {
  const { currentWorkspace } = useWorkspace();
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!currentWorkspace?.id) {
      setViews([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("custom-views/", {
        params: { workspace_id: currentWorkspace.id },
      });
      setViews(res.data || []);
    } catch (error) {
      console.error("Error fetching custom views:", error);
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const createView = useCallback(
    async ({ name, config }) => {
      const res = await api.post("custom-views/", {
        name,
        config,
        workspace: currentWorkspace.id,
      });
      await refetch();
      return res.data;
    },
    [currentWorkspace, refetch],
  );

  const updateView = useCallback(
    async (id, patch) => {
      const res = await api.patch(`custom-views/${id}/`, patch);
      await refetch();
      return res.data;
    },
    [refetch],
  );

  const deleteView = useCallback(
    async (id) => {
      await api.delete(`custom-views/${id}/`);
      await refetch();
    },
    [refetch],
  );

  return (
    <CustomViewsContext.Provider
      value={{ views, loading, refetch, createView, updateView, deleteView }}
    >
      {children}
    </CustomViewsContext.Provider>
  );
};

export const useCustomViews = () => {
  const context = useContext(CustomViewsContext);
  if (!context) {
    throw new Error("useCustomViews must be used within a CustomViewsProvider");
  }
  return context;
};
